import React, {
    useState,
    useEffect,
    useMemo,
    useCallback
} from "react";

import Swal from "sweetalert2";

import {
    Table,
    Button,
    Input,
    Select,
    Modal,
    Form,
    AutoComplete,
    InputNumber,
    Card,
    Row,
    Col,
    Tag,
    Space,
    Tooltip,
    Popconfirm,
    Alert,
    Spin,
    Empty,
    Divider,
    Typography
} from "antd";

import {
    SearchOutlined,
    PlusOutlined,
    DownloadOutlined,
    EditOutlined,
    DeleteOutlined,
    DollarOutlined,
    WalletOutlined,
    ReloadOutlined,
    FilterOutlined,
    LockOutlined,
    CheckCircleOutlined,
    WarningOutlined
} from "@ant-design/icons";

import {
    useAcademicYear
} from "../AcademicYearContext";

import api from "../api/api";

import {
    generatePaymentPDF
} from "./FinancialPaymentRecordPDF";

const {
    Title,
    Text
} = Typography;

const {
    Option
} = Select;




const fetchAllApiPages = async (
    endpoint,
    config = {},
    params = {}
) => {
    let nextUrl = endpoint;
    let nextParams = params;

    const allResults = [];
    const visitedUrls = new Set();

    while (nextUrl) {
        if (config.signal?.aborted) {
            throw new Error("Request cancelled");
        }

        // Detect repeated URLs to prevent an infinite loop.
        const requestKey = String(nextUrl);

        if (visitedUrls.has(requestKey)) {
            throw new Error(
                `Pagination loop detected while loading ${endpoint}`
            );
        }

        visitedUrls.add(requestKey);

        const response = await api.get(nextUrl, {
            ...config,
            ...(nextParams ? { params: nextParams } : {}),
        });

        if (config.signal?.aborted) {
            throw new Error("Request cancelled");
        }

        const data = response.data;

        // Plain array response.
        if (Array.isArray(data)) {
            allResults.push(...data);
            break;
        }

        // DRF paginated response.
        if (Array.isArray(data?.results)) {
            allResults.push(...data.results);

            if (data.next) {
                // Follow the exact next URL supplied by Django.
                nextUrl = data.next;
                nextParams = undefined;
                continue;
            }

            // Compatibility fallback for the custom paginator.
            const currentPage = Number(data.current_page || 1);
            const totalPages = Number(data.total_pages || 1);

            if (
                currentPage < totalPages &&
                Number.isFinite(totalPages)
            ) {
                nextUrl = endpoint;
                nextParams = {
                    ...params,
                    page: currentPage + 1,
                };
                continue;
            }

            break;
        }

        // Alternative API response format.
        if (Array.isArray(data?.data)) {
            allResults.push(...data.data);
            break;
        }

        throw new Error(
            `Unexpected API response format from ${endpoint}`
        );
    }

    // Deduplicate without excluding students with incomplete names.
    const uniqueResults = new Map();

    for (const item of allResults) {
        if (item?.id != null) {
            uniqueResults.set(String(item.id), item);
        }
    }

    return Array.from(uniqueResults.values());
};

const FinancialPaymentRecord = () => {
    const token = localStorage.getItem("access");

    const {
        selectedYear
    } = useAcademicYear();

    const [form] = Form.useForm();

    const [payments, setPayments] = useState([]);
    const [students, setStudents] = useState([]);
    const [grades, setGrades] = useState([]);
    const [schoolFeesData, setSchoolFeesData] = useState([]);
    const [
        selectedStudentPayments,
        setSelectedStudentPayments
    ] = useState([]);

    const [totalPayments, setTotalPayments] = useState(0);
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [loading, setLoading] = useState(false);
    const [
        supportingDataLoading,
        setSupportingDataLoading
    ] = useState(false);
    const [
        studentPaymentLoading,
        setStudentPaymentLoading
    ] = useState(false);

    const [search, setSearch] = useState("");
    const [selectedGrade, setSelectedGrade] = useState("");

    const [openDialog, setOpenDialog] = useState(false);
    const [editId, setEditId] = useState(null);

    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [
        maximumInstallmentAmount,
        setMaximumInstallmentAmount
    ] = useState(0);

    const [
        originalInstallmentAmount,
        setOriginalInstallmentAmount
    ] = useState(0);

    const [
        previouslyPaidAmount,
        setPreviouslyPaidAmount
    ] = useState(0);

    const [
        formData,
        setFormData
    ] = useState({
        semester: "",
        installment: "",
        student: "",
        student_class: "",
        student_type: "",
        total_fee: "",
        payer_name: "",
        amount_paid: "",
        payment_currency: "LRD",
        exchange_rate: "",
        date: ""
    });

    const [
        financialSummary,
        setFinancialSummary
    ] = useState({
        total_entries: 0,
        total_fee: 0,
        total_paid: 0,
        total_paid_lrd: 0,
        total_balance: 0
    });

    const selectedCurrency =
        Form.useWatch(
            "payment_currency",
            form
        ) ||
        formData.payment_currency ||
        "LRD";

    const currentExchangeRate = Number(
        Form.useWatch(
            "exchange_rate",
            form
        ) ||
        formData.exchange_rate ||
        0
    );

    const currentEnteredAmount = Number(
        Form.useWatch(
            "amount_paid",
            form
        ) ||
        formData.amount_paid ||
        0
    );

    const currentAmountLrd =
        selectedCurrency === "USD"
            ? currentEnteredAmount *
              currentExchangeRate
            : currentEnteredAmount;

    const formatMoney = useCallback(
        value => {
            return Number(
                value || 0
            ).toLocaleString(
                undefined,
                {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2
                }
            );
        },
        []
    );

    const normalizeStudentType = useCallback(
        value => {
            if (!value) {
                return "";
            }

            const normalized =
                String(value)
                    .trim()
                    .toLowerCase();

            if (
                normalized === "old" ||
                normalized === "old student"
            ) {
                return "old";
            }

            if (
                normalized === "new" ||
                normalized === "new student"
            ) {
                return "new";
            }

            if (
                normalized === "scholarship" ||
                normalized === "scholarship student"
            ) {
                return "scholarship"; 
            }

            if (
                normalized === "dependant_student" ||
                normalized === "dependent student"
            ) {
                return "dependent"; 
            }

            if (
                normalized === "ward" ||
                normalized === "ward student"
            ) {
                return "ward";
            }

            if (
                normalized === "dependent" ||
                normalized === "dependent student"
            ) {
                return "dependent";
            }

            return normalized;
        },
        []
    );

    const getPaymentCurrency = useCallback(
        payment => {
            return (
                payment?.payment_currency ||
                "LRD"
            ).toUpperCase();
        },
        []
    );

    const getPaymentAmountLrd = useCallback(
        payment => {
            const savedLrd = Number(
                payment?.amount_paid_lrd || 0
            );

            if (
                Number.isFinite(savedLrd) &&
                savedLrd > 0
            ) {
                return savedLrd;
            }

            const currency =
                getPaymentCurrency(
                    payment
                );

            const amount = Number(
                payment?.amount_paid || 0
            );

            if (
                currency === "USD"
            ) {
                const rate = Number(
                    payment?.exchange_rate || 0
                );

                if (
                    Number.isFinite(rate) &&
                    rate > 0
                ) {
                    return Number(
                        (
                            amount *
                            rate
                        ).toFixed(2)
                    );
                }
            }

            return amount;
        },
        [
            getPaymentCurrency
        ]
    );

    const convertToLrd = useCallback(
        (
            amount,
            currency,
            exchangeRate
        ) => {
            const numericAmount =
                Number(amount || 0);

            if (
                !Number.isFinite(
                    numericAmount
                )
            ) {
                return 0;
            }

            if (
                String(
                    currency || "LRD"
                ).toUpperCase() ===
                "LRD"
            ) {
                return Number(
                    numericAmount.toFixed(2)
                );
            }

            const rate = Number(
                exchangeRate || 0
            );

            if (
                !Number.isFinite(rate) ||
                rate <= 0
            ) {
                return 0;
            }

            return Number(
                (
                    numericAmount *
                    rate
                ).toFixed(2)
            );
        },
        []
    );

    const getMatchedFeeRecord = useCallback(
        (
            studentClass,
            studentType
        ) => {
            if (
                !studentClass ||
                !studentType
            ) {
                return null;
            }

            const normalizedType =
                normalizeStudentType(
                    studentType
                );

            return (
                schoolFeesData.find(
                    fee => {
                        const feeType =
                            normalizeStudentType(
                                fee.student_type
                            );

                        return (
                            Number(
                                fee.grade_class
                            ) ===
                                Number(
                                    studentClass
                                ) &&
                            feeType ===
                                normalizedType
                        );
                    }
                ) || null
            );
        },
        [
            schoolFeesData,
            normalizeStudentType
        ]
    );

    const getInstallmentAmounts = useCallback(
        feeRecord => {
            if (!feeRecord) {
                return {};
            }

            return {
                Registration:
                    Number(
                        feeRecord.registration ||
                        0
                    ),
                "1st Installment":
                    Number(
                        feeRecord.first_installment ||
                        0
                    ),
                "2nd Installment":
                    Number(
                        feeRecord.second_installment ||
                        0
                    ),
                "3rd Installment":
                    Number(
                        feeRecord.third_installment ||
                        0
                    ),
                "4th Installment":
                    Number(
                        feeRecord.fourth_installment ||
                        0
                    )
            };
        },
        []
    );

    const getPaymentInstallment = useCallback(
        payment => {
            return (
                payment?.installment ||
                ""
            ).trim();
        },
        []
    );

    const getAlreadyPaidForInstallment =
        useCallback(
            (
                installment,
                paymentsList = selectedStudentPayments,
                semester = null,
                excludePaymentId = null
            ) => {
                if (!installment) {
                    return 0;
                }

                return paymentsList
                    .filter(
                        payment => {
                            if (
                                getPaymentInstallment(
                                    payment
                                ) !==
                                installment
                            ) {
                                return false;
                            }

                            if (
                                semester &&
                                payment.semester !==
                                    semester
                            ) {
                                return false;
                            }

                            if (
                                excludePaymentId &&
                                Number(
                                    payment.id
                                ) ===
                                    Number(
                                        excludePaymentId
                                    )
                            ) {
                                return false;
                            }

                            return true;
                        }
                    )
                    .reduce(
                        (
                            total,
                            payment
                        ) =>
                            total +
                            getPaymentAmountLrd(
                                payment
                            ),
                        0
                    );
            },
            [
                selectedStudentPayments,
                getPaymentInstallment,
                getPaymentAmountLrd
            ]
        );

    const calculateInstallmentMaximum =
        useCallback(
            (
                studentId,
                studentClass,
                studentType,
                installment,
                paymentHistory =
                    selectedStudentPayments,
                semester = null,
                excludePaymentId = null
            ) => {
                if (
                    !studentId ||
                    !studentClass ||
                    !studentType ||
                    !installment
                ) {
                    return 0;
                }

                const feeRecord =
                    getMatchedFeeRecord(
                        studentClass,
                        studentType
                    );

                if (!feeRecord) {
                    return 0;
                }

                const amounts =
                    getInstallmentAmounts(
                        feeRecord
                    );

                let originalAmount =
                    Number(
                        amounts[
                            installment
                        ] || 0
                    );

                const student =
                    students.find(
                        item =>
                            Number(
                                item.id
                            ) ===
                            Number(
                                studentId
                            )
                    );

                const studentStatus =
                    String(
                        student?.student_status ||
                        ""
                    ).toLowerCase();

                if (
                    [
                        "dependant_student",
                        "dependent",
                        "scholarship",
                        "ward"
                    ].includes(
                        studentStatus
                    ) &&
                    installment !==
                        "Registration"
                ) {
                    originalAmount *= 0.5;
                }

                const alreadyPaid =
                    getAlreadyPaidForInstallment(
                        installment,
                        paymentHistory,
                        semester,
                        excludePaymentId
                    );

                return Number(
                    Math.max(
                        originalAmount -
                            alreadyPaid,
                        0
                    ).toFixed(2)
                );
            },
            [
                students,
                selectedStudentPayments,
                getMatchedFeeRecord,
                getInstallmentAmounts,
                getAlreadyPaidForInstallment
            ]
        );

    const getInstallmentInformation =
        useCallback(
            (
                studentId,
                studentClass,
                studentType,
                installment,
                paymentHistory,
                semester = null,
                excludePaymentId = null
            ) => {
                const feeRecord =
                    getMatchedFeeRecord(
                        studentClass,
                        studentType
                    );

                if (!feeRecord) {
                    return {
                        originalAmount: 0,
                        previouslyPaid: 0,
                        remainingBalance: 0
                    };
                }

                const amounts =
                    getInstallmentAmounts(
                        feeRecord
                    );

                let originalAmount =
                    Number(
                        amounts[
                            installment
                        ] || 0
                    );

                const student =
                    students.find(
                        item =>
                            Number(
                                item.id
                            ) ===
                            Number(
                                studentId
                            )
                    );

                const studentStatus =
                    String(
                        student?.student_status ||
                        ""
                    ).toLowerCase();

                if (
                    [
                        "dependant_student",
                        "dependent",
                        "scholarship",
                        "ward"
                    ].includes(
                        studentStatus
                    ) &&
                    installment !==
                        "Registration"
                ) {
                    originalAmount *= 0.5;
                }

                const alreadyPaid =
                    getAlreadyPaidForInstallment(
                        installment,
                        paymentHistory,
                        semester,
                        excludePaymentId
                    );

                const remainingBalance =
                    Math.max(
                        originalAmount -
                            alreadyPaid,
                        0
                    );

                return {
                    originalAmount:
                        Number(
                            originalAmount.toFixed(
                                2
                            )
                        ),
                    previouslyPaid:
                        Number(
                            alreadyPaid.toFixed(
                                2
                            )
                        ),
                    remainingBalance:
                        Number(
                            remainingBalance.toFixed(
                                2
                            )
                        )
                };
            },
            [
                students,
                getMatchedFeeRecord,
                getInstallmentAmounts,
                getAlreadyPaidForInstallment
            ]
        );

    const fetchStudentPaymentHistory =
        useCallback(
            async studentId => {
                if (
                    !studentId ||
                    !selectedYear ||
                    !token
                ) {
                    setSelectedStudentPayments(
                        []
                    );

                    return [];
                }

                try {
                    setStudentPaymentLoading(
                        true
                    );

                    const response =
                        await api.get(
                            "/api/users/payments/",
                            {
                                headers: {
                                    Authorization:
                                        `Bearer ${token}`
                                },
                                params: {
                                    academic_year:
                                        selectedYear.id,
                                    student_id:
                                        studentId,
                                    page_size:
                                        1000
                                }
                            }
                        );

                    let history =
                        response.data?.results ||
                        response.data ||
                        [];

                    if (
                        !Array.isArray(
                            history
                        )
                    ) {
                        history = [];
                    }

                    history =
                        history.filter(
                            payment =>
                                Number(
                                    payment.student
                                ) ===
                                Number(
                                    studentId
                                )
                        );

                    setSelectedStudentPayments(
                        history
                    );

                    return history;
                } catch (err) {
                    console.error(
                        "Failed to fetch student payment history:",
                        err
                    );

                    const fallback =
                        payments.filter(
                            payment =>
                                Number(
                                    payment.student
                                ) ===
                                Number(
                                    studentId
                                )
                        );

                    setSelectedStudentPayments(
                        fallback
                    );

                    return fallback;
                } finally {
                    setStudentPaymentLoading(
                        false
                    );
                }
            },
            [
                token,
                selectedYear,
                payments
            ]
        );

    const fetchPayments =
        useCallback(
            async () => {
                if (
                    !token ||
                    !selectedYear
                ) {
                    return;
                }

                try {
                    setLoading(true);
                    setError("");

                    const params = {
                        page,
                        page_size:
                            rowsPerPage,
                        academic_year:
                            selectedYear.id,
                        exclude_blacklisted:
                            true
                    };

                    if (search.trim()) {
                        params.search =
                            search.trim();
                    }

                    if (selectedGrade) {
                        params.student_class =
                            selectedGrade;
                    }

                    const response =
                        await api.get(
                            "/api/users/payments/",
                            {
                                headers: {
                                    Authorization:
                                        `Bearer ${token}`
                                },
                                params
                            }
                        );

                    const data =
                        response.data || {};

                    setPayments(
                        Array.isArray(
                            data.results
                        )
                            ? data.results
                            : Array.isArray(
                                  data
                              )
                            ? data
                            : []
                    );

                    setTotalPayments(
                        Number(
                            data.count ||
                                data.results?.length ||
                                0
                        )
                    );

                    setFinancialSummary(
                        data.summary || {
                            total_entries: 0,
                            total_fee: 0,
                            total_paid: 0,
                            total_paid_lrd: 0,
                            total_balance: 0
                        }
                    );
                } catch (err) {
                    console.error(
                        "Failed to load payment records:",
                        err
                    );

                    setError(
                        "Failed to load payment records."
                    );
                } finally {
                    setLoading(false);
                }
            },
            [
                token,
                selectedYear,
                page,
                rowsPerPage,
                search,
                selectedGrade
            ]
        );


const fetchSupportingData = useCallback(
    async (signal) => {
        if (!token || !selectedYear?.id) {
            setStudents([]);
            setGrades([]);
            setSchoolFeesData([]);
            setSupportingDataLoading(false);
            return;
        }

        try {
            setSupportingDataLoading(true);
            setError("");

            const config = {
                signal,
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            };

            const academicYearId = selectedYear.id;

            const [
                allStudents,
                gradesResponse,
                allFees,
            ] = await Promise.all([
                fetchAllApiPages(
                    "/api/users/students/",
                    config,
                    {
                        academic_year: academicYearId,
                        page_size: 100,
                    }
                ),

                api.get(
                    "/api/users/grade-classes/",
                    config
                ),

                fetchAllApiPages(
                    "/api/users/school-fees-data/",
                    config,
                    {
                        academic_year: academicYearId,
                        page_size: 100,
                    }
                ),
            ]);

            if (signal?.aborted) return;

            const gradesData = Array.isArray(
                gradesResponse.data
            )
                ? gradesResponse.data
                : gradesResponse.data?.results ??
                  gradesResponse.data?.data ??
                  [];

            // Keep all returned admission records, even if a name
            // is incomplete. The pagination helper already deduplicates IDs.
            const sortedStudents = [...allStudents].sort(
                (a, b) => {
                    const nameA = String(
                        a.full_name ||
                        `${a.first_name || ""} ${a.last_name || ""}`
                    ).trim();

                    const nameB = String(
                        b.full_name ||
                        `${b.first_name || ""} ${b.last_name || ""}`
                    ).trim();

                    return nameA.localeCompare(
                        nameB,
                        undefined,
                        { sensitivity: "base" }
                    );
                }
            );

            setStudents(sortedStudents);

            setGrades(
                Array.isArray(gradesData)
                    ? gradesData
                    : []
            );

            setSchoolFeesData(
                Array.isArray(allFees)
                    ? allFees
                    : []
            );

            if (sortedStudents.length === 0) {
                setError(
                    `No students were returned for academic year ${academicYearId}.`
                );
            }

            console.info(
                `[FinancialPaymentRecord] Loaded ${sortedStudents.length} students for academic year ${academicYearId}.`
            );
        } catch (err) {
            if (
                signal?.aborted ||
                err.name === "AbortError" ||
                err.code === "ERR_CANCELED"
            ) {
                return;
            }

            console.error(
                "Failed to load supporting data:",
                err
            );

            setStudents([]);
            setGrades([]);
            setSchoolFeesData([]);

            setError(
                err.response?.data?.detail ||
                err.response?.data?.error ||
                err.message ||
                "Failed to load student admission records. Please refresh and try again."
            );
        } finally {
            if (!signal?.aborted) {
                setSupportingDataLoading(false);
            }
        }
    },
    [token, selectedYear?.id]
);




useEffect(() => {
    const controller = new AbortController();

    fetchSupportingData(controller.signal);

    return () => {
        controller.abort();
    };
}, [fetchSupportingData]);


    useEffect(
        () => {
            if (selectedYear) {
                fetchPayments();
            }
        },
        [
            selectedYear,
            fetchPayments
        ]
    );



const studentOptions = useMemo(() => {
    return students.map((student) => {
        const fullName = String(
            student.full_name ||
            `${student.first_name || ""} ${student.last_name || ""}`
        ).trim() || "Unnamed Student";

        const admissionNumber =
            student.admission_number ||
            student.admission_no ||
            student.admission_id ||
            student.admission_number_display ||
            "";

        const gradeClass =
            student.grade_class_name ||
            student.grade_class?.name ||
            (typeof student.grade_class === "string"
                ? student.grade_class
                : "") ||
            "";

        const searchText = [
            fullName,
            admissionNumber,
            gradeClass,
            student.id,
        ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

        return {
            // Unique record identifier
            value: String(student.id),

            label: (
                <div className="payment-student-option">
                    <strong>{fullName}</strong>

                    {admissionNumber && (
                        <span>
                            Admission No: {admissionNumber}
                        </span>
                    )}

                    <span>
                        Grade Class: {gradeClass || "Not assigned"}
                    </span>
                </div>
            ),

            searchText,
            student,
        };
    });
}, [students]);


    const applyInstallmentBalance =
        useCallback(
            (
                studentId,
                studentClass,
                studentType,
                installment,
                paymentHistory,
                semester = null,
                excludePaymentId = null
            ) => {
                const information =
                    getInstallmentInformation(
                        studentId,
                        studentClass,
                        studentType,
                        installment,
                        paymentHistory,
                        semester,
                        excludePaymentId
                    );

                setOriginalInstallmentAmount(
                    information.originalAmount
                );

                setPreviouslyPaidAmount(
                    information.previouslyPaid
                );

                setMaximumInstallmentAmount(
                    information.remainingBalance
                );

                setFormData(
                    previous => ({
                        ...previous,
                        total_fee:
                            information.originalAmount.toFixed(
                                2
                            )
                    })
                );

                form.setFieldsValue({
                    total_fee:
                        information.originalAmount
                });

                if (!editId) {
                    form.setFieldsValue({
                        amount_paid:
                            undefined
                    });

                    setFormData(
                        previous => ({
                            ...previous,
                            amount_paid:
                                ""
                        })
                    );
                }
            },
            [
                getInstallmentInformation,
                form,
                editId
            ]
        );

    useEffect(
        () => {
            if (
                !openDialog ||
                !formData.student ||
                !formData.student_class ||
                !formData.student_type ||
                !formData.installment ||
                editId
            ) {
                return;
            }

            applyInstallmentBalance(
                formData.student,
                formData.student_class,
                formData.student_type,
                formData.installment,
                selectedStudentPayments,
                formData.semester || null
            );
        },
        [
            openDialog,
            formData.student,
            formData.student_class,
            formData.student_type,
            formData.installment,
            formData.semester,
            selectedStudentPayments,
            editId,
            applyInstallmentBalance
        ]
    );

    const handleDialogOpen =
        async payment => {
            if (payment) {
                setEditId(
                    payment.id
                );

                const selectedStudent =
                    students.find(
                        student =>
                            Number(
                                student.id
                            ) ===
                            Number(
                                payment.student
                            )
                    );

                let history =
                    payments.filter(
                        item =>
                            Number(
                                item.student
                            ) ===
                            Number(
                                payment.student
                            )
                    );

                if (
                    selectedYear &&
                    payment.student
                ) {
                    try {
                        const response =
                            await api.get(
                                "/api/users/payments/",
                                {
                                    headers: {
                                        Authorization:
                                            `Bearer ${token}`
                                    },
                                    params: {
                                        academic_year:
                                            selectedYear.id,
                                        student_id:
                                            payment.student,
                                        page_size:
                                            1000
                                    }
                                }
                            );

                        history =
                            (
                                response.data
                                    ?.results ||
                                response.data ||
                                []
                            ).filter(
                                item =>
                                    Number(
                                        item.student
                                    ) ===
                                    Number(
                                        payment.student
                                    )
                            );
                    } catch (err) {
                        console.error(
                            "Could not load payment history for editing:",
                            err
                        );
                    }
                }

                setSelectedStudentPayments(
                    history
                );

                const paymentCurrency =
                    getPaymentCurrency(
                        payment
                    );

                const paymentData = {
                    semester:
                        payment.semester ||
                        "",
                    installment:
                        payment.installment ||
                        "1st Installment",
                    student:
                        selectedStudent?.full_name ||
                        payment.student_name ||
                        "",
                    student_class:
                        payment.student_class,
                    student_type:
                        normalizeStudentType(
                            payment.student_type
                        ),
                    total_fee:
                        Number(
                            payment.total_fee ||
                                0
                        ),
                    payer_name:
                        payment.payer_name ||
                        "",
                    amount_paid:
                        Number(
                            payment.amount_paid ||
                                0
                        ),
                    payment_currency:
                        paymentCurrency,
                    exchange_rate:
                        paymentCurrency ===
                        "USD"
                            ? Number(
                                  payment.exchange_rate ||
                                      0
                              )
                            : "",
                    date:
                        payment.date ||
                        ""
                };

                setFormData({
                    ...paymentData,
                    student:
                        payment.student
                });

                form.setFieldsValue(
                    paymentData
                );

                const currentPaymentLrd =
                    getPaymentAmountLrd(
                        payment
                    );

                const maximum =
                    calculateInstallmentMaximum(
                        payment.student,
                        payment.student_class,
                        payment.student_type,
                        payment.installment,
                        history,
                        payment.semester,
                        payment.id
                    );

                const editableMaximum =
                    Number(
                        (
                            maximum +
                            currentPaymentLrd
                        ).toFixed(2)
                    );

                setMaximumInstallmentAmount(
                    editableMaximum
                );

                setOriginalInstallmentAmount(
                    editableMaximum
                );

                setPreviouslyPaidAmount(
                    Math.max(
                        editableMaximum -
                            currentPaymentLrd,
                        0
                    )
                );
            } else {
                setEditId(null);

                setSelectedStudentPayments(
                    []
                );

                setOriginalInstallmentAmount(
                    0
                );

                setPreviouslyPaidAmount(
                    0
                );

                setMaximumInstallmentAmount(
                    0
                );

                const emptyData = {
                    semester: "",
                    installment:
                        "1st Installment",
                    student: "",
                    student_class: "",
                    student_type: "",
                    total_fee: "",
                    payer_name: "",
                    amount_paid: "",
                    payment_currency:
                        "LRD",
                    exchange_rate:
                        "",
                    date: ""
                };

                setFormData(
                    emptyData
                );

                form.resetFields();

                form.setFieldsValue({
                    installment:
                        "1st Installment",
                    payment_currency:
                        "LRD"
                });
            }

            setOpenDialog(
                true
            );
        };

    const closeDialog = () => {
        setOpenDialog(
            false
        );

        setEditId(null);

        setMaximumInstallmentAmount(
            0
        );

        setOriginalInstallmentAmount(
            0
        );

        setPreviouslyPaidAmount(
            0
        );

        setSelectedStudentPayments(
            []
        );

        form.resetFields();

        setFormData({
            semester: "",
            installment:
                "1st Installment",
            student: "",
            student_class: "",
            student_type: "",
            total_fee: "",
            payer_name: "",
            amount_paid: "",
            payment_currency:
                "LRD",
            exchange_rate:
                "",
            date: ""
        });
    };



const handleStudentSelect = async (value, option) => {
    const student = option?.student;

    if (!student) return;

    const studentClass =
        student.grade_class_id ??
        student.grade_class?.id ??
        (typeof student.grade_class === "number"
            ? student.grade_class
            : "");

    const studentName =
        String(
            student.full_name ||
            `${student.first_name || ""} ${student.last_name || ""}`
        ).trim() || "Unnamed Student";

    setFormData((previous) => ({
        ...previous,
        student: student.id,
        student_class: studentClass,
    }));

    form.setFieldsValue({
        student: studentName,
        student_class: studentClass || undefined,
    });

    try {
        const history = await fetchStudentPaymentHistory(
            student.id
        );

        const previousPayment = [...history].sort(
            (a, b) => Number(b.id) - Number(a.id)
        )[0];

        const studentType = normalizeStudentType(
            previousPayment?.student_type ||
            student.student_type ||
            student.student_status ||
            ""
        );

        setFormData((previous) => ({
            ...previous,
            student: student.id,
            student_class: studentClass,
            student_type: studentType,
        }));

        form.setFieldsValue({
            student: studentName,
            student_class: studentClass || undefined,
            student_type: studentType || undefined,
        });

        if (studentClass && studentType) {
            applyInstallmentBalance(
                student.id,
                studentClass,
                studentType,
                form.getFieldValue("installment") ||
                    "1st Installment",
                history,
                form.getFieldValue("semester") || null
            );
        }
    } catch (err) {
        console.error(
            "Failed to load selected student:",
            err
        );
    }
};


    const handleSemesterChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    semester:
                        value
                })
            );

            form.setFieldsValue({
                semester:
                    value
            });

            const studentId = formData.student;

            const studentClass =
                formData.student_class;

            const studentType =
                formData.student_type;

            const installment =
                form.getFieldValue(
                    "installment"
                ) ||
                formData.installment;

            if (
                studentId &&
                studentClass &&
                studentType &&
                installment
            ) {
                applyInstallmentBalance(
                    studentId,
                    studentClass,
                    studentType,
                    installment,
                    selectedStudentPayments,
                    value
                );
            }
        };

    const handleInstallmentChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    installment:
                        value,
                    amount_paid:
                        ""
                })
            );

            form.setFieldsValue({
                installment:
                    value,
                amount_paid:
                    undefined
            });

            if (
                formData.student &&
                formData.student_class &&
                formData.student_type
            ) {
                applyInstallmentBalance(
                    formData.student,
                    formData.student_class,
                    formData.student_type,
                    value,
                    selectedStudentPayments,
                    formData.semester ||
                        null,
                    editId || null
                );
            }
        };

    const handleClassChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    student_class:
                        value
                })
            );

            form.setFieldsValue({
                student_class:
                    value
            });

            if (
                formData.student &&
                formData.student_type &&
                formData.installment
            ) {
                applyInstallmentBalance(
                    formData.student,
                    value,
                    formData.student_type,
                    formData.installment,
                    selectedStudentPayments,
                    formData.semester ||
                        null,
                    editId || null
                );
            }
        };

    const handleStudentTypeChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    student_type:
                        value
                })
            );

            form.setFieldsValue({
                student_type:
                    value
            });

            if (
                formData.student &&
                formData.student_class &&
                formData.installment
            ) {
                applyInstallmentBalance(
                    formData.student,
                    formData.student_class,
                    value,
                    formData.installment,
                    selectedStudentPayments,
                    formData.semester ||
                        null,
                    editId || null
                );
            }
        };

    const handleCurrencyChange =
        value => {
            const currentRate =
                form.getFieldValue(
                    "exchange_rate"
                );

            setFormData(
                previous => ({
                    ...previous,
                    payment_currency:
                        value,
                    exchange_rate:
                        value ===
                        "USD"
                            ? previous.exchange_rate
                            : ""
                })
            );

            form.setFieldsValue({
                payment_currency:
                    value,
                exchange_rate:
                    value ===
                    "USD"
                        ? currentRate
                        : undefined
            });
        };

    const handleExchangeRateChange =
        value => {
            setFormData(
                previous => ({
                    ...previous,
                    exchange_rate:
                        value || ""
                })
            );

            form.setFieldsValue({
                exchange_rate:
                    value
            });
        };

    const handleAmountChange =
        (
            value,
            forcedCurrency = null
        ) => {
            if (
                value === null ||
                value === undefined ||
                value === ""
            ) {
                setFormData(
                    previous => ({
                        ...previous,
                        amount_paid:
                            ""
                    })
                );

                return;
            }

            const enteredAmount =
                Number(value);

            const currency =
                forcedCurrency ||
                form.getFieldValue(
                    "payment_currency"
                ) ||
                formData.payment_currency ||
                "LRD";

            const exchangeRate =
                Number(
                    form.getFieldValue(
                        "exchange_rate"
                    ) ||
                        formData.exchange_rate ||
                        0
                );

            if (
                currency === "USD" &&
                exchangeRate <= 0
            ) {
                setFormData(
                    previous => ({
                        ...previous,
                        amount_paid:
                            value
                    })
                );

                return;
            }

            const enteredAmountLrd =
                convertToLrd(
                    enteredAmount,
                    currency,
                    exchangeRate
                );

            const maximum =
                Number(
                    maximumInstallmentAmount
                );

            if (
                maximum >= 0 &&
                enteredAmountLrd >
                    maximum
            ) {
                const maximumInCurrency =
                    currency ===
                    "USD"
                        ? Number(
                              (
                                  maximum /
                                  exchangeRate
                              ).toFixed(
                                  2
                              )
                          )
                        : maximum;

                Swal.fire({
                    icon:
                        "error",
                    title:
                        "Payment Amount Exceeded",
                    html: `
                        <div style="font-size:15px;line-height:1.8;text-align:left;">
                            <div>
                                The amount entered exceeds the student's
                                current installment balance.
                            </div>

                            <br/>

                            <strong>Installment:</strong>
                            ${form.getFieldValue("installment") || formData.installment}

                            <br/>

                            <strong>Current balance:</strong>
                            ${formatMoney(maximum)} LRD

                            <br/>

                            <strong>Maximum in ${currency}:</strong>
                            ${formatMoney(maximumInCurrency)} ${currency}

                            <br/>

                            <strong>Amount entered:</strong>
                            ${formatMoney(enteredAmount)} ${currency}
                        </div>
                    `,
                    confirmButtonColor:
                        "#ff6b00"
                });

                form.setFieldsValue({
                    amount_paid:
                        maximumInCurrency
                });

                setFormData(
                    previous => ({
                        ...previous,
                        amount_paid:
                            maximumInCurrency
                    })
                );

                return;
            }

            setFormData(
                previous => ({
                    ...previous,
                    amount_paid:
                        value
                })
            );
        };


const handleSubmit = async (values) => {
    if (!token) {
        Swal.fire({
            icon: "error",
            title: "Authentication Required",
            text: "No authentication token was found.",
            confirmButtonColor: "#ff6b00",
        });
        return;
    }

    if (!selectedYear) {
        Swal.fire({
            icon: "error",
            title: "Academic Year Required",
            text: "Please select an academic year.",
            confirmButtonColor: "#ff6b00",
        });
        return;
    }

    try {
        setLoading(true);

        // FIX: Initialize studentId BEFORE using it.
        const rawStudentId =
            formData.student || values.student;

        const studentId = Number(rawStudentId);

        const selectedStudent = students.find(
            (student) => Number(student.id) === studentId
        );

        if (
            !rawStudentId ||
            !Number.isFinite(studentId) ||
            !selectedStudent
        ) {
            Swal.fire({
                icon: "error",
                title: "Student Required",
                text: "Please select a valid student before recording payment.",
                confirmButtonColor: "#ff6b00",
            });
            return;
        }

        let currentHistory = selectedStudentPayments;

        if (!editId) {
            currentHistory =
                await fetchStudentPaymentHistory(studentId);
        }

        const studentClass = Number(
            values.student_class || formData.student_class
        );

        const studentType = normalizeStudentType(
            values.student_type || formData.student_type
        );

        const installment =
            values.installment || formData.installment;

        const semester =
            values.semester || formData.semester;

        const paymentCurrency = String(
            values.payment_currency ||
                formData.payment_currency ||
                "LRD"
        ).toUpperCase();

        const exchangeRate = Number(
            values.exchange_rate ||
                formData.exchange_rate ||
                0
        );

        const amountPaid = Number(values.amount_paid || 0);

        if (!Number.isFinite(amountPaid) || amountPaid <= 0) {
            Swal.fire({
                icon: "error",
                title: "Invalid Amount",
                text: "Please enter an amount greater than zero.",
                confirmButtonColor: "#ff6b00",
            });
            return;
        }

        if (
            paymentCurrency === "USD" &&
            (!Number.isFinite(exchangeRate) || exchangeRate <= 0)
        ) {
            Swal.fire({
                icon: "error",
                title: "Exchange Rate Required",
                text: "Please enter a valid USD to LRD exchange rate.",
                confirmButtonColor: "#ff6b00",
            });
            return;
        }

        const amountPaidLrd = convertToLrd(
            amountPaid,
            paymentCurrency,
            exchangeRate
        );

        let installmentLimit = calculateInstallmentMaximum(
            studentId,
            studentClass,
            studentType,
            installment,
            currentHistory,
            semester,
            editId
        );

        if (editId) {
            const existingPayment = currentHistory.find(
                (payment) => Number(payment.id) === Number(editId)
            );

            if (existingPayment) {
                installmentLimit = Number(
                    (
                        installmentLimit +
                        getPaymentAmountLrd(existingPayment)
                    ).toFixed(2)
                );
            }
        }

        if (installmentLimit <= 0 && !editId) {
            Swal.fire({
                icon: "warning",
                title: "No Balance Remaining",
                text: "This installment has already been fully paid.",
                confirmButtonColor: "#ff6b00",
            });
            return;
        }

        if (amountPaidLrd > installmentLimit) {
            const maximumInCurrency =
                paymentCurrency === "USD"
                    ? Number(
                          (installmentLimit / exchangeRate).toFixed(2)
                      )
                    : installmentLimit;

            Swal.fire({
                icon: "error",
                title: "Payment Amount Exceeded",
                html: `
                    <div style="font-size:15px;line-height:1.8;text-align:left;">
                        <div>
                            This payment cannot be greater than
                            the student's current balance.
                        </div>
                        <br/>
                        <strong>Installment:</strong> ${installment}
                        <br/>
                        <strong>Current balance:</strong>
                        ${formatMoney(installmentLimit)} LRD
                        <br/>
                        <strong>Maximum payment:</strong>
                        ${formatMoney(maximumInCurrency)} ${paymentCurrency}
                        <br/>
                        <strong>Amount entered:</strong>
                        ${formatMoney(amountPaid)} ${paymentCurrency}
                        <br/>
                        <strong>LRD equivalent:</strong>
                        ${formatMoney(amountPaidLrd)} LRD
                    </div>
                `,
                confirmButtonColor: "#ff6b00",
            });
            return;
        }

        const payload = {
            ...values,
            student: studentId,
            academic_year: selectedYear.id,
            student_class: studentClass,
            student_type: studentType,
            installment,
            semester,
            amount_paid: amountPaid,
            payment_currency: paymentCurrency,
            exchange_rate:
                paymentCurrency === "USD" ? exchangeRate : null,
        };

        if (!editId) {
            payload.total_fee = Number(installmentLimit.toFixed(2));
        }

        if (editId) {
            await api.put(
                `/api/users/payments/${editId}/`,
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSuccess("Payment record updated successfully.");

            await Swal.fire({
                icon: "success",
                title: "Payment Updated",
                text: "The payment record was updated successfully.",
                confirmButtonColor: "#ff6b00",
                timer: 1800,
                timerProgressBar: true,
            });
        } else {
            await api.post(
                "/api/users/payments/",
                payload,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSuccess("Payment record added successfully.");

            await Swal.fire({
                icon: "success",
                title: "Payment Saved",
                text: "The payment record was added successfully.",
                confirmButtonColor: "#ff6b00",
                timer: 1800,
                timerProgressBar: true,
            });
        }

        closeDialog();
        await fetchPayments();
    } catch (err) {
        console.error("Payment save error:", err);

        const responseData = err?.response?.data;
        let message = "Failed to save the payment record.";

        if (responseData) {
            const errorFields = [
                "amount_paid",
                "exchange_rate",
                "payment_currency",
                "total_fee",
                "detail",
                "non_field_errors",
            ];

            let foundError = false;

            for (const key of errorFields) {
                if (responseData[key]) {
                    const errorValue = responseData[key];
                    message = Array.isArray(errorValue)
                        ? errorValue[0]
                        : String(errorValue);
                    foundError = true;
                    break;
                }
            }

            if (!foundError) {
                const firstErrorKey = Object.keys(responseData)[0];

                if (firstErrorKey) {
                    const firstError = responseData[firstErrorKey];
                    message = Array.isArray(firstError)
                        ? firstError[0]
                        : String(firstError);
                }
            }
        } else if (err?.message) {
            message = err.message;
        }

        Swal.fire({
            icon: "error",
            title: "Invalid Payment",
            text: message,
            confirmButtonColor: "#ff6b00",
        });
    } finally {
        setLoading(false);
    }
};


    const handleDelete =
        async id => {
            const result =
                await Swal.fire({
                    title:
                        "Delete Payment Record?",
                    text:
                        "This action cannot be undone.",
                    icon:
                        "warning",
                    showCancelButton:
                        true,
                    confirmButtonText:
                        "Yes, Delete",
                    cancelButtonText:
                        "Cancel",
                    confirmButtonColor:
                        "#ff6b00",
                    cancelButtonColor:
                        "#111111"
                });

            if (
                !result.isConfirmed
            ) {
                return;
            }

            try {
                setLoading(true);

                await api.delete(
                    `/api/users/payments/${id}/`,
                    {
                        headers: {
                            Authorization:
                                `Bearer ${token}`
                        }
                    }
                );

                await Swal.fire({
                    icon:
                        "success",
                    title:
                        "Deleted",
                    text:
                        "Payment record deleted successfully.",
                    confirmButtonColor:
                        "#ff6b00",
                    timer:
                        1600,
                    timerProgressBar:
                        true
                });

                await fetchPayments();
            } catch (err) {
                console.error(
                    "Delete payment error:",
                    err
                );

                Swal.fire({
                    icon:
                        "error",
                    title:
                        "Delete Failed",
                    text:
                        "The payment record could not be deleted.",
                    confirmButtonColor:
                        "#ff6b00"
                });
            } finally {
                setLoading(false);
            }
        };

    const handleDownloadPDF =
        () => {
            try {
                generatePaymentPDF({
                    selectedGrade,
                    grades,
                    token,
                    api,
                    setLoading,
                    setError
                });
            } catch (err) {
                console.error(
                    "PDF generation error:",
                    err
                );

                setError(
                    "Unable to generate the payment PDF."
                );
            }
        };

    const resetFilters =
        () => {
            setSearch("");
            setSelectedGrade("");
            setPage(1);
        };

    const getStudentName =
        record => {
            return (
                record?.student_name ||
                record?.student_full_name ||
                record?.student_details?.full_name ||
                students.find(
                    student =>
                        Number(
                            student.id
                        ) ===
                        Number(
                            record?.student
                        )
                )?.full_name ||
                "N/A"
            );
        };

    const getClassName =
        record => {
            return (
                record?.student_class_name ||
                record?.grade_class_name ||
                record?.student_class_details?.name ||
                grades.find(
                    grade =>
                        Number(
                            grade.id
                        ) ===
                        Number(
                            record?.student_class
                        )
                )?.name ||
                "N/A"
            );
        };

    const columns = [
        {
            title:
                "PAYMENT TYPE",
            dataIndex:
                "installment",
            key:
                "installment",
            fixed:
                "left",
            width:
                175,
            render:
                value => (
                    <Tag
                        color="orange"
                        style={{
                            fontWeight:
                                700,
                            borderRadius:
                                20,
                            padding:
                                "5px 13px"
                        }}
                    >
                        {
                            value ||
                            "N/A"
                        }
                    </Tag>
                )
        },
        {
            title:
                "STUDENT",
            dataIndex:
                "student_name",
            key:
                "student_name",
            width:
                220,
            render:
                (value, record) => (
                    <div>
                        <Text strong>
                            {
                                value ||
                                getStudentName(
                                    record
                                )
                            }
                        </Text>

                        {record?.admission_id && (
                            <div>
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            11
                                    }}
                                >
                                    {
                                        record.admission_id
                                    }
                                </Text>
                            </div>
                        )}
                    </div>
                )
        },
        {
            title:
                "SEMESTER",
            dataIndex:
                "semester",
            key:
                "semester",
            width:
                120,
            render:
                value =>
                    value ? (
                        <Tag>
                            {value}
                        </Tag>
                    ) : (
                        "-"
                    )
        },
        {
            title:
                "CLASS",
            dataIndex:
                "student_class_name",
            key:
                "student_class_name",
            width:
                170,
            render:
                (value, record) =>
                    value ||
                    getClassName(
                        record
                    )
        },
        {
            title:
                "STUDENT TYPE",
            dataIndex:
                "student_type",
            key:
                "student_type",
            width:
                145,
            render:
                value => {
                    const normalized =
                        normalizeStudentType(
                            value
                        );

                    const colors = {
                        new:
                            "green",
                        old:
                            "blue",
                        scholarship:
                            "purple",
                        ward:
                            "gold",
                        dependent:
                            "cyan"
                    };

                    return (
                        <Tag
                            color={
                                colors[
                                    normalized
                                ] ||
                                "default"
                            }
                        >
                            {
                                value ||
                                "N/A"
                            }
                        </Tag>
                    );
                }
        },
        {
            title:
                "TOTAL FEE",
            dataIndex:
                "total_fee",
            key:
                "total_fee",
            width:
                160,
            align:
                "right",
            render:
                value => (
                    <Text strong>
                        {formatMoney(
                            value
                        )}{" "}
                        LRD
                    </Text>
                )
        },
        {
            title:
                "AMOUNT PAID",
            dataIndex:
                "amount_paid",
            key:
                "amount_paid",
            width:
                190,
            align:
                "right",
            render:
                (value, record) => {
                    const currency =
                        getPaymentCurrency(
                            record
                        );

                    const amount =
                        Number(
                            value || 0
                        );

                    const amountLrd =
                        getPaymentAmountLrd(
                            record
                        );

                    return (
                        <div
                            style={{
                                display:
                                    "flex",
                                flexDirection:
                                    "column",
                                alignItems:
                                    "flex-end"
                            }}
                        >
                            <Text
                                strong
                                style={{
                                    color:
                                        "#16803c"
                                }}
                            >
                                {formatMoney(
                                    amount
                                )}{" "}
                                {currency}
                            </Text>

                            {currency ===
                                "USD" && (
                                <Text
                                    type="secondary"
                                    style={{
                                        fontSize:
                                            11
                                    }}
                                >
                                    ≈{" "}
                                    {formatMoney(
                                        amountLrd
                                    )}{" "}
                                    LRD
                                </Text>
                            )}
                        </div>
                    );
                }
        },
        {
            title:
                "BALANCE",
            dataIndex:
                "balance",
            key:
                "balance",
            width:
                155,
            align:
                "right",
            render:
                (value, record) => {
                    const balance =
                        Number(
                            value ??
                                (
                                    Number(
                                        record?.total_fee ||
                                            0
                                    ) -
                                    Number(
                                        record?.amount_paid_lrd ||
                                            getPaymentAmountLrd(
                                                record
                                            )
                                    )
                                )
                        );

                    return (
                        <Text
                            strong
                            style={{
                                color:
                                    balance >
                                    0
                                        ? "#c2410c"
                                        : "#16803c"
                            }}
                        >
                            {formatMoney(
                                Math.max(
                                    balance,
                                    0
                                )
                            )}{" "}
                            LRD
                        </Text>
                    );
                }
        },
        {
            title:
                "PAYER",
            dataIndex:
                "payer_name",
            key:
                "payer_name",
            width:
                190,
            render:
                value =>
                    value ||
                    "N/A"
        },
        {
            title:
                "DATE",
            dataIndex:
                "date",
            key:
                "date",
            width:
                125,
            render:
                value =>
                    value
                        ? new Date(
                              value
                          ).toLocaleDateString()
                        : "-"
        },
        {
            title:
                "ACTIONS",
            key:
                "actions",
            fixed:
                "right",
            width:
                135,
            render:
                (_, record) => (
                    <Space
                        size="small"
                    >
                        <Tooltip title="Edit payment">
                            <Button
                                type="text"
                                icon={
                                    <EditOutlined />
                                }
                                onClick={() =>
                                    handleDialogOpen(
                                        record
                                    )
                                }
                                style={{
                                    color:
                                        "#C0750D"
                                }}
                            />
                        </Tooltip>

                        <Popconfirm
                            title="Delete payment?"
                            description="This action cannot be undone."
                            okText="Delete"
                            cancelText="Cancel"
                            okButtonProps={{
                                danger:
                                    true
                            }}
                            onConfirm={() =>
                                handleDelete(
                                    record.id
                                )
                            }
                        >
                            <Tooltip title="Delete payment">
                                <Button
                                    type="text"
                                    danger
                                    icon={
                                        <DeleteOutlined />
                                    }
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Space>
                )
        }
    ];

    const summaryEntries =
        Number(
            financialSummary.total_entries ||
                totalPayments ||
                0
        );

    const summaryFee =
        Number(
            financialSummary.total_fee ||
                0
        );

    const summaryPaid =
        Number(
            financialSummary.total_paid_lrd ??
                financialSummary.total_paid ??
                0
        );

    const summaryBalance =
        Number(
            financialSummary.total_balance ||
                Math.max(
                    summaryFee -
                        summaryPaid,
                    0
                )
        );

    return (
        <div
            className="financial-payment-page"
            style={{
                minHeight:
                    "100vh",
                padding:
                    "22px",
                background:
                    "linear-gradient(180deg,#f6f8fb 0%,#eef2f6 100%)"
            }}
        >
            <div className="financial-shell">
                <div className="financial-hero">
                    <div className="financial-hero-main">
                        <div className="financial-hero-copy">
                            <div className="financial-kicker">
                                <span className="financial-kicker-dot" />
                                FINANCIAL MANAGEMENT
                            </div>

                            <div className="financial-title-row">
                                <div className="financial-title-icon">
                                    <WalletOutlined />
                                </div>

                                <div>
                                    <Title className="financial-title">
                                        Financial Payment Records
                                    </Title>

                                    <Text className="financial-subtitle">
                                        Manage student payments,
                                        installments, balances,
                                        currencies, and financial
                                        records.
                                    </Text>
                                </div>
                            </div>

                            <div className="financial-year-badge">
                                <span>
                                    ACTIVE ACADEMIC YEAR
                                </span>

                                <strong>
                                    {selectedYear?.name ||
                                        selectedYear?.year ||
                                        selectedYear?.academic_year ||
                                        "Not Selected"}
                                </strong>
                            </div>
                        </div>

                        <div className="financial-hero-actions">
                            <Button
                                icon={
                                    <ReloadOutlined />
                                }
                                className="financial-secondary-button"
                                onClick={() => {
                                    fetchPayments();
                                    fetchSupportingData();
                                }}
                                loading={
                                    loading ||
                                    supportingDataLoading
                                }
                            >
                                Refresh
                            </Button>

                            <Button
                                type="primary"
                                icon={
                                    <PlusOutlined />
                                }
                                className="financial-primary-button"
                                onClick={() =>
                                    handleDialogOpen()
                                }
                            >
                                New Payment
                            </Button>
                        </div>
                    </div>

                    <div className="financial-hero-strip">
                        <div>
                            <strong>
                                {summaryEntries.toLocaleString()}
                            </strong>
                            <span>
                                Payment Entries
                            </span>
                        </div>

                        <div>
                            <strong>
                                {formatMoney(
                                    summaryPaid
                                )}{" "}
                                LRD
                            </strong>
                            <span>
                                Total Collected
                            </span>
                        </div>

                        <div>
                            <strong>
                                {formatMoney(
                                    summaryBalance
                                )}{" "}
                                LRD
                            </strong>
                            <span>
                                Outstanding Balance
                            </span>
                        </div>
                    </div>
                </div>

                <div className="financial-summary-grid">
                    <Card className="financial-summary-card">
                        <div className="financial-summary-icon financial-summary-orange">
                            <WalletOutlined />
                        </div>

                        <div className="financial-summary-content">
                            <span>
                                Total Entries
                            </span>

                            <strong>
                                {summaryEntries.toLocaleString()}
                            </strong>

                            <small>
                                Recorded payments
                            </small>
                        </div>
                    </Card>

                    <Card className="financial-summary-card">
                        <div className="financial-summary-icon financial-summary-blue">
                            <DollarOutlined />
                        </div>

                        <div className="financial-summary-content">
                            <span>
                                Total Fees
                            </span>

                            <strong>
                                {formatMoney(
                                    summaryFee
                                )}{" "}
                                LRD
                            </strong>

                            <small>
                                Expected fees
                            </small>
                        </div>
                    </Card>

                    <Card className="financial-summary-card">
                        <div className="financial-summary-icon financial-summary-green">
                            <CheckCircleOutlined />
                        </div>

                        <div className="financial-summary-content">
                            <span>
                                Total Paid
                            </span>

                            <strong>
                                {formatMoney(
                                    summaryPaid
                                )}{" "}
                                LRD
                            </strong>

                            <small>
                                Collected payments
                            </small>
                        </div>
                    </Card>

                    <Card className="financial-summary-card">
                        <div className="financial-summary-icon financial-summary-red">
                            <WarningOutlined />
                        </div>

                        <div className="financial-summary-content">
                            <span>
                                Balance
                            </span>

                            <strong>
                                {formatMoney(
                                    summaryBalance
                                )}{" "}
                                LRD
                            </strong>

                            <small>
                                Remaining amount
                            </small>
                        </div>
                    </Card>
                </div>

                {error && (
                    <Alert
                        message={
                            error
                        }
                        type="error"
                        showIcon
                        closable
                        onClose={() =>
                            setError("")
                        }
                        style={{
                            marginBottom:
                                18,
                            borderRadius:
                                12
                        }}
                    />
                )}

                {success && (
                    <Alert
                        message={
                            success
                        }
                        type="success"
                        showIcon
                        closable
                        onClose={() =>
                            setSuccess("")
                        }
                        style={{
                            marginBottom:
                                18,
                            borderRadius:
                                12
                        }}
                    />
                )}

                <Card className="financial-control-card">
                    <div className="financial-section-heading">
                        <div>
                            <div className="financial-section-label">
                                RECORD SEARCH
                            </div>

                            <Title level={4}>
                                Find Payment Records
                            </Title>
                        </div>

                        <Tag className="financial-record-count">
                            {totalPayments.toLocaleString()} Records
                        </Tag>
                    </div>

                    <div className="financial-filter-grid">
                        <div className="financial-filter-wide">
                            <Input
                                allowClear
                                size="large"
                                prefix={
                                    <SearchOutlined />
                                }
                                placeholder="Search student, payer, admission number..."
                                value={
                                    search
                                }
                                onChange={event => {
                                    setSearch(
                                        event.target.value
                                    );
                                    setPage(
                                        1
                                    );
                                }}
                            />
                        </div>

                        <Select
                            allowClear
                            size="large"
                            placeholder="Filter by grade/class"
                            value={
                                selectedGrade ||
                                undefined
                            }
                            onChange={value => {
                                setSelectedGrade(
                                    value ||
                                        ""
                                );
                                setPage(
                                    1
                                );
                            }}
                            style={{
                                width:
                                    "100%"
                            }}
                            suffixIcon={
                                <FilterOutlined />
                            }
                        >
                            {grades.map(
                                grade => (
                                    <Option
                                        key={
                                            grade.id
                                        }
                                        value={
                                            grade.id
                                        }
                                    >
                                        {grade.name ||
                                            grade.grade_name ||
                                            grade.title}
                                    </Option>
                                )
                            )}
                        </Select>

                        <Button
                            size="large"
                            className="financial-reset-button"
                            icon={
                                <ReloadOutlined />
                            }
                            onClick={
                                resetFilters
                            }
                        >
                            Reset Filters
                        </Button>
                    </div>
                </Card>

                <Card
                    className="financial-table-card"
                    bodyStyle={{
                        padding:
                            0
                    }}
                >
                    <div className="financial-table-header">
                        <div>
                            <div className="financial-section-label">
                                PAYMENT REGISTER
                            </div>

                            <Title
                                level={4}
                            >
                                Student Payment Records
                            </Title>
                        </div>

                        <Space wrap>
                            <span className="financial-table-status">
                                <span className="financial-status-dot" />
                                Live Academic Year Data
                            </span>

                            <Button
                                icon={
                                    <DownloadOutlined />
                                }
                                onClick={
                                    handleDownloadPDF
                                }
                            >
                                PDF
                            </Button>

                            <Button
                                type="primary"
                                icon={
                                    <PlusOutlined />
                                }
                                onClick={() =>
                                    handleDialogOpen()
                                }
                            >
                                Add Payment
                            </Button>
                        </Space>
                    </div>

                    <div className="financial-table-wrap">
                        <Table
                            rowKey={record =>
                                record.id
                            }
                            columns={
                                columns
                            }
                            dataSource={
                                payments
                            }
                            loading={
                                loading
                            }
                            scroll={{
                                x:
                                    1700
                            }}
                            locale={{
                                emptyText:
                                    loading ? (
                                        <Spin />
                                    ) : (
                                        <Empty
                                            description="No payment records found"
                                        />
                                    )
                            }}
                            rowClassName={() =>
                                "payment-table-row"
                            }
                            pagination={{
                                current:
                                    page,
                                pageSize:
                                    rowsPerPage,
                                total:
                                    totalPayments,
                                showSizeChanger:
                                    true,
                                showQuickJumper:
                                    true,
                                pageSizeOptions:
                                    [
                                        "10",
                                        "25",
                                        "50",
                                        "100"
                                    ],
                                showTotal:
                                    total =>
                                        `Total ${total} records`,
                                onChange:
                                    (
                                        nextPage,
                                        nextPageSize
                                    ) => {
                                        setPage(
                                            nextPage
                                        );

                                        if (
                                            nextPageSize !==
                                            rowsPerPage
                                        ) {
                                            setRowsPerPage(
                                                nextPageSize
                                            );
                                            setPage(
                                                1
                                            );
                                        }
                                    }
                            }}
                        />
                    </div>
                </Card>

                <Modal
                    open={
                        openDialog
                    }
                    onCancel={
                        closeDialog
                    }
                    footer={null}
                    width={
                        900
                    }
                    centered
                    destroyOnClose={
                        false
                    }
                    className="financial-payment-modal"
                    title={
                        <div className="financial-modal-title">
                            <div className="financial-modal-icon">
                                <DollarOutlined />
                            </div>

                            <div>
                                <div className="financial-modal-heading">
                                    {editId
                                        ? "Edit Payment Record"
                                        : "Create Payment Record"}
                                </div>

                                <Text type="secondary">
                                    {editId
                                        ? "Update the existing payment information."
                                        : "Record a new student payment."}
                                </Text>
                            </div>
                        </div>
                    }
                >
                    <Form
                        form={
                            form
                        }
                        layout="vertical"
                        onFinish={
                            handleSubmit
                        }
                        preserve={
                            true
                        }
                    >
                        <div className="financial-modal-scroll">
                            <div className="financial-form-section">
                                <div className="financial-form-section-title">
                                    <span>
                                        01
                                    </span>
                                    Payment Information
                                </div>

                                <Row
                                    gutter={[
                                        16,
                                        4
                                    ]}
                                >
                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="Semester"
                                            name="semester"
                                        >
                                            <Select
                                                size="large"
                                                placeholder="Select semester"
                                                onChange={
                                                    handleSemesterChange
                                                }
                                                allowClear
                                            >
                                                <Option value="First">
                                                    Semester One
                                                </Option>

                                                <Option value="Second">
                                                    Semester Two
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="Payment label or Installment"
                                            name="installment"
                                            rules={[
                                                {
                                                    required:
                                                        true,
                                                    message:
                                                        "Select the label or installment"
                                                }
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                onChange={
                                                    handleInstallmentChange
                                                }
                                            >
                                                <Option value="Registration">
                                                    Registration
                                                </Option>

                                                <Option value="1st Installment">
                                                    1st Installment
                                                </Option>

                                                <Option value="2nd Installment">
                                                    2nd Installment
                                                </Option>

                                                <Option value="3rd Installment">
                                                    3rd Installment
                                                </Option>

                                                <Option value="4th Installment">
                                                    4th Installment
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row
                                    gutter={[
                                        16,
                                        4
                                    ]}
                                >
                                    <Col
                                        xs={
                                            24
                                        }
                                    >
                                        <Form.Item
                                            label="Student"
                                            name="student"
                                            rules={[
                                                {
                                                    required:
                                                        true,
                                                    message:
                                                        "Select a student"
                                                }
                                            ]}
                                        >



<AutoComplete
    size="large"
    options={studentOptions}
    placeholder={
        supportingDataLoading
            ? "Loading registered students..."
            : students.length > 0
                ? `Search ${students.length} registered students...`
                : "No registered students available"
    }
    onSelect={handleStudentSelect}
    disabled={supportingDataLoading || students.length === 0}
    allowClear
    style={{ width: "100%" }}
    listHeight={360}
    popupMatchSelectWidth={true}
    filterOption={(inputValue, option) => {
        const query = String(inputValue || "")
            .trim()
            .toLowerCase();

        if (!query) return true;

        const searchableText = String(
            option?.searchText ||
            option?.label ||
            option?.value ||
            ""
        ).toLowerCase();

        return searchableText.includes(query);
    }}
    notFoundContent={
        supportingDataLoading ? (
            <div
                style={{
                    padding: "20px",
                    textAlign: "center",
                }}
            >
                <Spin size="small" />
                <div style={{ marginTop: 8 }}>
                    Loading registered students...
                </div>
            </div>
        ) : (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                    students.length === 0
                        ? "No students loaded. Check the selected academic year and API response."
                        : "No students match your search."
                }
            />
        )
    }
/>



                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Row
                                    gutter={[
                                        16,
                                        4
                                    ]}
                                >
                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="Student Class"
                                            name="student_class"
                                            rules={[
                                                {
                                                    required:
                                                        true,
                                                    message:
                                                        "Select student class"
                                                }
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                showSearch
                                                optionFilterProp="children"
                                                placeholder="Select class"
                                                onChange={
                                                    handleClassChange
                                                }
                                            >
                                                {grades.map(
                                                    grade => (
                                                        <Option
                                                            key={
                                                                grade.id
                                                            }
                                                            value={
                                                                grade.id
                                                            }
                                                        >
                                                            {grade.name ||
                                                                grade.grade_name ||
                                                                grade.title}
                                                        </Option>
                                                    )
                                                )}
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="Student Payment Type"
                                            name="student_type"
                                            rules={[
                                                {
                                                    required:
                                                        true,
                                                    message:
                                                        "Select Student Payment Type"
                                                }
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                placeholder="Select Student Payment Type"
                                                onChange={
                                                    handleStudentTypeChange
                                                }
                                            >
                                          

                                                <Option value="old">
                                                    Old Student
                                                </Option>

                                                <Option value="new">
                                                    New Student
                                                </Option>

                                                <Option value="dependant_student">
                                                    Dependent Student
                                                </Option>

                                                <Option value="scholarship">
                                                    Scholarship
                                                </Option>

                                                <Option value="ward">
                                                    Ward
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Card className="financial-balance-card">
                                    <div className="financial-balance-item">
                                        <span>
                                            Installment Fee
                                        </span>

                                        <strong>
                                            {formatMoney(
                                                originalInstallmentAmount
                                            )}{" "}
                                            LRD
                                        </strong>
                                    </div>

                                    <div className="financial-balance-divider" />

                                    <div className="financial-balance-item">
                                        <span>
                                            Previously Paid
                                        </span>

                                        <strong className="financial-balance-paid">
                                            {formatMoney(
                                                previouslyPaidAmount
                                            )}{" "}
                                            LRD
                                        </strong>
                                    </div>

                                    <div className="financial-balance-divider" />

                                    <div className="financial-balance-item">
                                        <span>
                                            Remaining Balance
                                        </span>

                                        <strong
                                            className={
                                                maximumInstallmentAmount >
                                                0
                                                    ? "financial-balance-due"
                                                    : "financial-balance-zero"
                                            }
                                        >
                                            {formatMoney(
                                                maximumInstallmentAmount
                                            )}{" "}
                                            LRD
                                        </strong>
                                    </div>
                                </Card>

                                {studentPaymentLoading && (
                                    <Alert
                                        type="info"
                                        showIcon
                                        icon={
                                            <Spin size="small" />
                                        }
                                        message="Loading payment history..."
                                        style={{
                                            marginBottom:
                                                18
                                        }}
                                    />
                                )}

                                <Divider />

                                <div className="financial-form-section-title">
                                    <span>
                                        02
                                    </span>
                                    Payment Details
                                </div>

                                <Row
                                    gutter={[
                                        16,
                                        4
                                    ]}
                                >
                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="Payment Currency"
                                            name="payment_currency"
                                            rules={[
                                                {
                                                    required:
                                                        true,
                                                    message:
                                                        "Select payment currency"
                                                }
                                            ]}
                                        >
                                            <Select
                                                size="large"
                                                onChange={
                                                    handleCurrencyChange
                                                }
                                            >
                                                <Option value="LRD">
                                                    LRD — Liberian Dollar
                                                </Option>

                                                <Option value="USD">
                                                    USD — US Dollar
                                                </Option>
                                            </Select>
                                        </Form.Item>
                                    </Col>

                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="USD to LRD Exchange Rate"
                                            name="exchange_rate"
                                            rules={[
                                                {
                                                    validator:
                                                        (
                                                            _,
                                                            value
                                                        ) => {
                                                            const currency =
                                                                form.getFieldValue(
                                                                    "payment_currency"
                                                                );

                                                            if (
                                                                currency !==
                                                                "USD"
                                                            ) {
                                                                return Promise.resolve();
                                                            }

                                                            if (
                                                                value ===
                                                                    undefined ||
                                                                value ===
                                                                    null ||
                                                                value ===
                                                                    "" ||
                                                                Number(
                                                                    value
                                                                ) <=
                                                                    0
                                                            ) {
                                                                return Promise.reject(
                                                                    new Error(
                                                                        "Enter the current USD to LRD exchange rate"
                                                                    )
                                                                );
                                                            }

                                                            return Promise.resolve();
                                                        }
                                                }
                                            ]}
                                        >
                                            <InputNumber
                                                size="large"
                                                min={
                                                    0
                                                }
                                                precision={
                                                    4
                                                }
                                                suffix="LRD/USD"
                                                style={{
                                                    width:
                                                        "100%"
                                                }}
                                                placeholder="Example: 170"
                                                disabled={
                                                    selectedCurrency !==
                                                    "USD"
                                                }
                                                onChange={
                                                    handleExchangeRateChange
                                                }
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    label="Amount Paid"
                                    name="amount_paid"
                                    rules={[
                                        {
                                            required:
                                                true,
                                            message:
                                                "Enter amount paid"
                                        },
                                        {
                                            validator:
                                                (
                                                    _,
                                                    value
                                                ) => {
                                                    if (
                                                        Number(
                                                            value ||
                                                                0
                                                        ) <=
                                                        0
                                                    ) {
                                                        return Promise.reject(
                                                            new Error(
                                                                "Amount must be greater than zero"
                                                            )
                                                        );
                                                    }

                                                    return Promise.resolve();
                                                }
                                        }
                                    ]}
                                >
                                    <InputNumber
                                        size="large"
                                        min={
                                            0
                                        }
                                        precision={
                                            2
                                        }
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                        prefix={
                                            selectedCurrency ===
                                            "USD"
                                                ? "$"
                                                : "LD"
                                        }
                                        placeholder={
                                            selectedCurrency ===
                                            "USD"
                                                ? "Enter USD amount"
                                                : "Enter LRD amount"
                                        }
                                        onChange={
                                            value =>
                                                handleAmountChange(
                                                    value
                                                )
                                        }
                                    />
                                </Form.Item>

                                {selectedCurrency ===
                                    "USD" &&
                                    currentExchangeRate >
                                        0 &&
                                    currentEnteredAmount >
                                        0 && (
                                        <Alert
                                            type="success"
                                            showIcon
                                            icon={
                                                <DollarOutlined />
                                            }
                                            message="USD payment conversion"
                                            description={
                                                <>
                                                    <strong>
                                                        {formatMoney(
                                                            currentEnteredAmount
                                                        )}{" "}
                                                        USD
                                                    </strong>

                                                    {" × "}

                                                    <strong>
                                                        {formatMoney(
                                                            currentExchangeRate
                                                        )}{" "}
                                                        LRD/USD
                                                    </strong>

                                                    {" = "}

                                                    <strong>
                                                        {formatMoney(
                                                            currentAmountLrd
                                                        )}{" "}
                                                        LRD
                                                    </strong>
                                                </>
                                            }
                                            style={{
                                                marginBottom:
                                                    20,
                                                borderRadius:
                                                    12
                                            }}
                                        />
                                    )}

                                <Row
                                    gutter={[
                                        16,
                                        4
                                    ]}
                                >
                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="Payer Name"
                                            name="payer_name"
                                            rules={[
                                                {
                                                    required:
                                                        true,
                                                    message:
                                                        "Enter payer name"
                                                }
                                            ]}
                                        >
                                            <Input
                                                size="large"
                                                placeholder="Enter name of person making payment"
                                            />
                                        </Form.Item>
                                    </Col>

                                    <Col
                                        xs={
                                            24
                                        }
                                        sm={
                                            12
                                        }
                                    >
                                        <Form.Item
                                            label="Payment Date"
                                            name="date"
                                            rules={[
                                                {
                                                    required:
                                                        true,
                                                    message:
                                                        "Select payment date"
                                                }
                                            ]}
                                        >
                                            <Input
                                                size="large"
                                                type="date"
                                            />
                                        </Form.Item>
                                    </Col>
                                </Row>

                                <Form.Item
                                    label="Calculated Installment Fee"
                                    name="total_fee"
                                >
                                    <InputNumber
                                        size="large"
                                        disabled
                                        prefix="LD"
                                        style={{
                                            width:
                                                "100%"
                                        }}
                                        precision={
                                            2
                                        }
                                    />
                                </Form.Item>

                                {maximumInstallmentAmount >
                                    0 && (
                                    <Alert
                                        type="info"
                                        showIcon
                                        icon={
                                            <WarningOutlined />
                                        }
                                        message={
                                            editId
                                                ? "Editing payment amount"
                                                : "Current installment balance"
                                        }
                                        description={
                                            editId ? (
                                                <>
                                                    This payment is being evaluated against the installment in LRD.
                                                    Previous paid amount:{" "}
                                                    <strong>
                                                        {formatMoney(
                                                            previouslyPaidAmount
                                                        )}{" "}
                                                        LRD
                                                    </strong>
                                                    . Current editable balance:{" "}
                                                    <strong>
                                                        {formatMoney(
                                                            maximumInstallmentAmount
                                                        )}{" "}
                                                        LRD
                                                    </strong>
                                                    .
                                                </>
                                            ) : (
                                                <>
                                                    The student's current balance for{" "}
                                                    <strong>
                                                        {
                                                            formData.installment
                                                        }
                                                    </strong>{" "}
                                                    is{" "}
                                                    <strong>
                                                        {formatMoney(
                                                            maximumInstallmentAmount
                                                        )}{" "}
                                                        LRD
                                                    </strong>
                                                    .
                                                </>
                                            )
                                        }
                                        style={{
                                            marginBottom:
                                                20,
                                            borderRadius:
                                                12
                                        }}
                                    />
                                )}

                                {formData.student &&
                                    formData.student_type &&
                                    maximumInstallmentAmount ===
                                        0 &&
                                    !editId && (
                                        <Alert
                                            type="warning"
                                            showIcon
                                            message="No remaining balance"
                                            description={
                                                <>
                                                    This student has already fully paid the{" "}
                                                    <strong>
                                                        {
                                                            formData.installment
                                                        }
                                                    </strong>
                                                    . No additional payment can be posted for this installment.
                                                </>
                                            }
                                            style={{
                                                marginBottom:
                                                    20,
                                                borderRadius:
                                                    12
                                            }}
                                        />
                                    )}
                            </div>

                            <div className="financial-modal-footer">
                                <Button
                                    size="large"
                                    onClick={
                                        closeDialog
                                    }
                                    className="financial-cancel-button"
                                >
                                    Cancel
                                </Button>

                                <Button
                                    size="large"
                                    htmlType="submit"
                                    type="primary"
                                    loading={
                                        loading ||
                                        studentPaymentLoading
                                    }
                                    disabled={
                                        !editId &&
                                        maximumInstallmentAmount <=
                                            0
                                    }
                                    icon={
                                        editId ? (
                                            <EditOutlined />
                                        ) : (
                                            <PlusOutlined />
                                        )
                                    }
                                    className="financial-save-button"
                                >
                                    {editId
                                        ? "Update Payment"
                                        : "Save Payment"}
                                </Button>
                            </div>
                        </div>
                    </Form>
                </Modal>

                <style>
                    {`
                        .financial-payment-page {
                            box-sizing: border-box;
                            color: #142536;
                        }

                        .financial-payment-page *,
                        .financial-payment-page *::before,
                        .financial-payment-page *::after {
                            box-sizing: border-box;
                        }

                        .financial-shell {
                            width: 100%;
                            max-width: 1800px;
                            margin: 0 auto;
                        }

                        .financial-hero {
                            overflow: hidden;
                            border-radius: 24px !important;
                            background:
                                radial-gradient(circle at 88% 10%, rgba(246,201,68,.22), transparent 28%),
                                linear-gradient(135deg,#062A4B 0%,#063A6B 48%,#0A0F12 100%);
                            box-shadow: 0 18px 45px rgba(6,42,75,.16);
                            margin-bottom: 18px;
                        }

                        .financial-hero-main {
                            min-height: 235px;
                            padding: 30px 32px;
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 30px;
                        }

                        .financial-hero-copy {
                            min-width: 0;
                        }

                        .financial-kicker {
                            display: flex;
                            align-items: center;
                            gap: 8px;
                            color: #F6C944;
                            font-size: 11px;
                            font-weight: 800;
                            letter-spacing: 1.4px;
                            margin-bottom: 15px;
                        }

                        .financial-kicker-dot {
                            width: 8px;
                            height: 8px;
                            border-radius: 50%;
                            background: #F6C944;
                            box-shadow: 0 0 0 5px rgba(246,201,68,.12);
                        }

                        .financial-title-row {
                            display: flex;
                            align-items: center;
                            gap: 16px;
                        }

                        .financial-title-icon {
                            width: 58px;
                            height: 58px;
                            flex: 0 0 58px;
                            border-radius: 17px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            color: #062A4B;
                            background: linear-gradient(135deg,#F6C944,#EFA81C);
                            font-size: 27px;
                            box-shadow: 0 10px 24px rgba(0,0,0,.18);
                        }

                        .financial-title {
                            margin: 0 !important;
                            color: #fff !important;
                            font-size: clamp(24px,3vw,34px) !important;
                            line-height: 1.15 !important;
                            font-weight: 800 !important;
                            letter-spacing: -.5px;
                        }

                        .financial-subtitle {
                            display: block;
                            margin-top: 8px;
                            color: rgba(255,255,255,.72) !important;
                            font-size: 14px;
                            line-height: 1.65;
                        }

                        .financial-year-badge {
                            display: inline-flex;
                            align-items: center;
                            gap: 11px;
                            margin-top: 22px;
                            padding: 9px 13px;
                            border: 1px solid rgba(255,255,255,.14);
                            border-radius: 10px;
                            background: rgba(255,255,255,.07);
                        }

                        .financial-year-badge span {
                            color: rgba(255,255,255,.52);
                            font-size: 9px;
                            font-weight: 800;
                            letter-spacing: 1px;
                        }

                        .financial-year-badge strong {
                            color: #F6C944;
                            font-size: 13px;
                        }

                        .financial-hero-actions {
                            display: flex;
                            align-items: center;
                            justify-content: flex-end;
                            gap: 10px;
                            flex-shrink: 0;
                        }

                        .financial-primary-button,
                        .financial-secondary-button {
                            height: 46px !important;
                            border-radius: 11px !important;
                            padding: 0 17px !important;
                            font-weight: 800 !important;
                        }

                        .financial-primary-button {
                            color: #062A4B !important;
                            background: #F6C944 !important;
                            border-color: #F6C944 !important;
                            box-shadow: 0 8px 20px rgba(246,201,68,.18);
                        }

                        .financial-primary-button:hover {
                            color: #062A4B !important;
                            background: #ffd95c !important;
                            border-color: #ffd95c !important;
                        }

                        .financial-secondary-button {
                            color: #fff !important;
                            background: rgba(255,255,255,.08) !important;
                            border-color: rgba(255,255,255,.22) !important;
                        }

                        .financial-secondary-button:hover {
                            color: #fff !important;
                            border-color: #F6C944 !important;
                        }

                        .financial-hero-strip {
                            min-height: 70px;
                            display: grid;
                            grid-template-columns: repeat(3,1fr);
                            border-top: 1px solid rgba(255,255,255,.10);
                            background: rgba(0,0,0,.16);
                        }

                        .financial-hero-strip > div {
                            display: flex;
                            flex-direction: column;
                            justify-content: center;
                            padding: 12px 28px;
                            border-right: 1px solid rgba(255,255,255,.08);
                        }

                        .financial-hero-strip > div:last-child {
                            border-right: 0;
                        }

                        .financial-hero-strip strong {
                            color: #fff;
                            font-size: 17px;
                            line-height: 1.2;
                        }

                        .financial-hero-strip span {
                            margin-top: 4px;
                            color: rgba(255,255,255,.52);
                            font-size: 10px;
                            font-weight: 700;
                            text-transform: uppercase;
                            letter-spacing: .8px;
                        }

                        .financial-summary-grid {
                            display: grid;
                            grid-template-columns: repeat(4,minmax(0,1fr));
                            gap: 14px;
                            margin-bottom: 18px;
                        }

                        .financial-summary-card {
                            min-height: 122px;
                            border-radius: 17px !important;
                            border: 1px solid #e3e9ee !important;
                            box-shadow: 0 8px 25px rgba(15,23,42,.045);
                        }

                        .financial-summary-card .ant-card-body {
                            height: 100%;
                            display: flex;
                            align-items: center;
                            gap: 14px;
                            padding: 20px !important;
                        }

                        .financial-summary-icon {
                            width: 46px;
                            height: 46px;
                            flex: 0 0 46px;
                            border-radius: 14px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-size: 20px;
                        }

                        .financial-summary-orange {
                            color: #C0750D;
                            background: #fff4e6;
                        }

                        .financial-summary-green {
                            color: #16803c;
                            background: #edf9f1;
                        }

                        .financial-summary-red {
                            color: #c2410c;
                            background: #fff1ed;
                        }

                        .financial-summary-blue {
                            color: #0F6DAB;
                            background: #edf7fd;
                        }

                        .financial-summary-content {
                            min-width: 0;
                            display: flex;
                            flex-direction: column;
                        }

                        .financial-summary-content span {
                            color: #6B7280;
                            font-size: 11px;
                            font-weight: 800;
                            text-transform: uppercase;
                            letter-spacing: .65px;
                        }

                        .financial-summary-content strong {
                            margin-top: 5px;
                            color: #142536;
                            font-size: 20px;
                            line-height: 1.2;
                            white-space: nowrap;
                            overflow: hidden;
                            text-overflow: ellipsis;
                        }

                        .financial-summary-content small {
                            margin-top: 4px;
                            color: #94a3b8;
                            font-size: 11px;
                        }

                        .financial-control-card,
                        .financial-table-card {
                            border-radius: 18px !important;
                            border: 1px solid #e3e9ee !important;
                            box-shadow: 0 8px 25px rgba(15,23,42,.045);
                            margin-bottom: 18px;
                        }

                        .financial-control-card .ant-card-body {
                            padding: 22px !important;
                        }

                        .financial-section-heading,
                        .financial-table-header {
                            display: flex;
                            align-items: center;
                            justify-content: space-between;
                            gap: 18px;
                        }

                        .financial-section-heading {
                            margin-bottom: 18px;
                        }

                        .financial-section-heading h4,
                        .financial-table-header h4 {
                            margin: 3px 0 2px !important;
                            color: #142536;
                            font-weight: 800;
                        }

                        .financial-section-label {
                            color: #C0750D;
                            font-size: 10px;
                            font-weight: 900;
                            letter-spacing: 1.15px;
                        }

                        .financial-record-count {
                            margin: 0;
                            padding: 6px 11px;
                            color: #063A6B !important;
                            background: #edf5fa !important;
                            border: 1px solid #d7e8f2 !important;
                            border-radius: 20px;
                            font-weight: 800;
                        }

                        .financial-filter-grid {
                            display: grid;
                            grid-template-columns: minmax(260px,1.7fr) minmax(210px,1fr) minmax(160px,.55fr);
                            gap: 12px;
                            align-items: center;
                        }

                        .financial-filter-wide {
                            min-width: 0;
                        }

                        .financial-filter-grid .ant-input-affix-wrapper,
                        .financial-filter-grid .ant-select-selector,
                        .financial-reset-button {
                            border-radius: 11px !important;
                        }

                        .financial-filter-grid .ant-input-affix-wrapper,
                        .financial-filter-grid .ant-select-selector {
                            min-height: 44px !important;
                            border-color: #dbe3e9 !important;
                        }

                        .financial-filter-grid .ant-input-affix-wrapper-focused,
                        .financial-filter-grid .ant-select-focused .ant-select-selector {
                            border-color: #C0750D !important;
                            box-shadow: 0 0 0 2px rgba(192,117,13,.10) !important;
                        }

                        .financial-filter-grid .ant-input-prefix,
                        .financial-filter-grid .ant-select-arrow {
                            color: #C0750D !important;
                        }

                        .financial-reset-button {
                            height: 44px !important;
                            color: #334155 !important;
                            border-color: #dbe3e9 !important;
                            font-weight: 700;
                            background: #fff !important;
                        }

                        .financial-reset-button:hover {
                            color: #C0750D !important;
                            border-color: #C0750D !important;
                        }

                        .financial-table-card {
                            overflow: hidden;
                        }

                        .financial-table-header {
                            padding: 20px 22px;
                            border-bottom: 1px solid #e7edf1;
                            background: #fff;
                        }

                        .financial-table-status {
                            display: inline-flex;
                            align-items: center;
                            gap: 7px;
                            padding: 7px 11px;
                            color: #475569;
                            background: #f7f9fa;
                            border: 1px solid #e5eaee;
                            border-radius: 20px;
                            font-size: 11px;
                            font-weight: 700;
                        }

                        .financial-status-dot {
                            width: 7px;
                            height: 7px;
                            border-radius: 50%;
                            background: #16803c;
                            box-shadow: 0 0 0 4px rgba(22,128,60,.10);
                        }

                        .financial-table-wrap {
                            width: 100%;
                        }

                        .financial-table-wrap .ant-table {
                            border-radius: 0 !important;
                        }

                        .financial-table-wrap .ant-table-thead > tr > th {
                            background: #062A4B !important;
                            color: #fff !important;
                            border-bottom: 0 !important;
                            padding-top: 14px !important;
                            padding-bottom: 14px !important;
                            font-size: 11px !important;
                            font-weight: 800 !important;
                            letter-spacing: .45px;
                            white-space: nowrap;
                        }

                        .financial-table-wrap .ant-table-thead > tr > th::before {
                            display: none !important;
                        }

                        .financial-table-wrap .ant-table-tbody > tr > td {
                            padding-top: 15px !important;
                            padding-bottom: 15px !important;
                            border-bottom-color: #edf1f4 !important;
                        }

                        .payment-table-row:hover > td {
                            background: #fff8eb !important;
                        }

                        .financial-table-wrap .ant-pagination {
                            padding: 8px 18px 18px;
                        }

                        .financial-table-wrap .ant-pagination-item-active {
                            border-color: #C0750D !important;
                        }

                        .financial-table-wrap .ant-pagination-item-active a {
                            color: #C0750D !important;
                        }

                        .financial-table-wrap .ant-btn-primary {
                            background: #062A4B;
                            border-color: #062A4B;
                        }

                        .financial-payment-modal .ant-modal-content {
                            padding: 0 !important;
                            overflow: hidden;
                            border-radius: 22px !important;
                        }

                        .financial-payment-modal .ant-modal-header {
                            padding: 22px 24px !important;
                            margin: 0 !important;
                            border-bottom: 0 !important;
                        }

                        .financial-modal-title {
                            display: flex;
                            align-items: center;
                            gap: 13px;
                        }

                        .financial-modal-icon {
                            width: 45px;
                            height: 45px;
                            flex: 0 0 45px;
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            border-radius: 13px;
                            color: #062A4B;
                            background: linear-gradient(135deg,#F6C944,#EFA81C);
                            font-size: 20px;
                        }

                        .financial-modal-heading {
                            color: #142536;
                            font-size: 18px;
                            font-weight: 850;
                            line-height: 1.2;
                        }

                        .financial-modal-scroll {
                            max-height: 70vh;
                            overflow-y: auto;
                            padding: 22px 24px 0;
                        }

                        .financial-form-section {
                            margin-bottom: 8px;
                        }

                        .financial-form-section-title {
                            display: flex;
                            align-items: center;
                            gap: 9px;
                            margin: 3px 0 16px;
                            color: #062A4B;
                            font-size: 12px;
                            font-weight: 900;
                            letter-spacing: .8px;
                            text-transform: uppercase;
                        }

                        .financial-form-section-title span {
                            width: 27px;
                            height: 27px;
                            display: inline-flex;
                            align-items: center;
                            justify-content: center;
                            border-radius: 8px;
                            color: #062A4B;
                            background: #f8e9b0;
                            font-size: 10px;
                        }

                        .financial-payment-modal .ant-form-item {
                            margin-bottom: 17px;
                        }

                        .financial-payment-modal .ant-form-item-label > label {
                            color: #334155;
                            font-size: 12px;
                            font-weight: 750;
                        }

                        .financial-payment-modal .ant-input,
                        .financial-payment-modal .ant-input-affix-wrapper,
                        .financial-payment-modal .ant-select-selector,
                        .financial-payment-modal .ant-input-number {
                            border-radius: 10px !important;
                            border-color: #dbe3e9 !important;
                        }

                        .financial-payment-modal .ant-input:focus,
                        .financial-payment-modal .ant-input-affix-wrapper-focused,
                        .financial-payment-modal .ant-input-number-focused,
                        .financial-payment-modal .ant-select-focused .ant-select-selector {
                            border-color: #C0750D !important;
                            box-shadow: 0 0 0 2px rgba(192,117,13,.10) !important;
                        }

                        .financial-balance-card {
                            margin-bottom: 18px;
                            border: 1px solid #eadfbf !important;
                            border-radius: 13px !important;
                            background: linear-gradient(135deg,#fffdf5,#fff8e8) !important;
                        }

                        .financial-balance-card .ant-card-body {
                            display: grid;
                            grid-template-columns: 1fr auto 1fr auto 1fr;
                            align-items: center;
                            gap: 14px;
                            padding: 15px !important;
                        }

                        .financial-balance-item {
                            min-width: 0;
                            display: flex;
                            flex-direction: column;
                            gap: 5px;
                        }

                        .financial-balance-item span {
                            color: #7b8794;
                            font-size: 10px;
                            font-weight: 800;
                            text-transform: uppercase;
                            letter-spacing: .7px;
                        }

                        .financial-balance-item strong {
                            color: #142536;
                            font-size: 14px;
                        }

                        .financial-balance-paid {
                            color: #16803c !important;
                        }

                        .financial-balance-due {
                            color: #c2410c !important;
                            font-size: 17px !important;
                        }

                        .financial-balance-zero {
                            color: #cf1322 !important;
                            font-size: 17px !important;
                        }

                        .financial-balance-divider {
                            width: 1px;
                            height: 38px;
                            background: #e5d8b3;
                        }

                        .financial-modal-footer {
                            position: sticky;
                            bottom: 0;
                            z-index: 3;
                            display: flex;
                            justify-content: flex-end;
                            gap: 10px;
                            margin: 10px -24px 0;
                            padding: 15px 24px;
                            border-top: 1px solid #e8edf1;
                            background: rgba(255,255,255,.97);
                            backdrop-filter: blur(10px);
                        }

                        .financial-cancel-button,
                        .financial-save-button {
                            height: 44px !important;
                            border-radius: 10px !important;
                            padding: 0 18px !important;
                            font-weight: 800 !important;
                        }

                        .financial-cancel-button {
                            border-color: #dbe3e9 !important;
                        }

                        .financial-save-button {
                            background: #C0750D !important;
                            border-color: #C0750D !important;
                        }

                        .financial-save-button:hover {
                            background: #a9650b !important;
                            border-color: #a9650b !important;
                        }

                        
.payment-student-option {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding: 5px 0;
    white-space: normal;
    line-height: 1.4;
}

.payment-student-option strong {
    color: #062a4b;
    font-size: 13px;
    font-weight: 700;
}

.payment-student-option span {
    color: #647586;
    font-size: 11px;
}

.ant-select-dropdown {
    max-height: 400px;
}

.ant-select-dropdown .rc-virtual-list-holder {
    max-height: 360px;
    overflow-y: auto;
}


                        @media (max-width: 1100px) {
                            .financial-summary-grid {
                                grid-template-columns: repeat(2,minmax(0,1fr));
                            }

                            .financial-filter-grid {
                                grid-template-columns: 1fr 1fr;
                            }

                            .financial-filter-wide {
                                grid-column: 1 / -1;
                            }

                            .financial-hero-main {
                                align-items: flex-start;
                            }
                        }

                        @media (max-width: 760px) {
                            .financial-payment-page {
                                padding: 10px !important;
                            }

                            .financial-hero {
                                border-radius: 18px !important;
                            }

                            .financial-hero-main {
                                min-height: 0;
                                padding: 22px 18px;
                                flex-direction: column;
                                gap: 22px;
                            }

                            .financial-title-row {
                                align-items: flex-start;
                            }

                            .financial-title-icon {
                                width: 48px;
                                height: 48px;
                                flex-basis: 48px;
                                border-radius: 14px;
                                font-size: 22px;
                            }

                            .financial-title {
                                font-size: 23px !important;
                            }

                            .financial-subtitle {
                                font-size: 12px;
                            }

                            .financial-year-badge {
                                width: 100%;
                                justify-content: space-between;
                            }

                            .financial-hero-actions {
                                width: 100%;
                                display: grid;
                                grid-template-columns: 1fr 1fr;
                            }

                            .financial-hero-actions button {
                                width: 100%;
                            }

                            .financial-hero-strip {
                                grid-template-columns: 1fr;
                            }

                            .financial-hero-strip > div {
                                border-right: 0;
                                border-bottom: 1px solid rgba(255,255,255,.08);
                                padding: 11px 18px;
                            }

                            .financial-hero-strip > div:last-child {
                                border-bottom: 0;
                            }

                            .financial-summary-grid {
                                grid-template-columns: 1fr;
                            }

                            .financial-control-card .ant-card-body {
                                padding: 16px !important;
                            }

                            .financial-section-heading,
                            .financial-table-header {
                                align-items: flex-start;
                                flex-direction: column;
                            }

                            .financial-filter-grid {
                                grid-template-columns: 1fr;
                            }

                            .financial-filter-wide {
                                grid-column: auto;
                            }

                            .financial-table-header {
                                padding: 17px;
                            }

                            .financial-modal-scroll {
                                max-height: 72vh;
                                padding: 18px 16px 0;
                            }

                            .financial-payment-modal .ant-modal-header {
                                padding: 18px 16px !important;
                            }

                            .financial-balance-card .ant-card-body {
                                grid-template-columns: 1fr;
                                gap: 11px;
                            }

                            .financial-balance-divider {
                                width: 100%;
                                height: 1px;
                            }

                            .financial-modal-footer {
                                margin-left: -16px;
                                margin-right: -16px;
                                padding-left: 16px;
                                padding-right: 16px;
                            }
                        }

                        @media (prefers-reduced-motion: reduce) {
                            .financial-payment-page * {
                                scroll-behavior: auto !important;
                                transition: none !important;
                            }
                        }
                    `}
                </style>
            </div>
        </div>
    );
};

export default FinancialPaymentRecord;